import { CHANNEL_ID, MEMBER_ID, POSITION_ID } from '@@types/common';
import Log from '@@types/Log';
import Member, { SharedChannelMember } from '@@types/Member';
import MemberResponse from '@@types/Member/Response';
import { PositionRef } from '@@types/Position';
import { TenantInvitedMemberResponse } from '@@types/Tenant/TenantInvitedMemberResponse';
import { Options } from '@popperjs/core';

import { MemberStatus, MODAL_NAMES, PopperType, PROFILE_TYPE } from '~/constants';
import { SECOND } from '~/constants/date';
import { isSharedChannel } from '~/services/channelService';
import FetchCache from '~/services/FetchCache';
import GA from '~/services/GA/GA';
import WebsocketActions from '~/stores/actions/WebsocketActions';
import { channelsStore } from '~/stores/ChannelsStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { membersStore } from '~/stores/MembersStore';
import { organizationChartStore } from '~/stores/OrganizationChartStore';
import { api } from '~/stores/providers/Api2';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { defer, rename } from '~/utils/fnUtil/fnUtil';
import { maybeOr, runFutureValue, then } from '~/utils/monadUtil';
import { isNative } from '~/utils/runEnvironmentUtil';
import { encaseP, FutureInstance } from 'fluture';
import { debounce, isEmpty, throttle } from 'lodash';
import { prop, reject } from 'ramda';
import curry from 'ramda/es/curry';
import defaultTo from 'ramda/es/defaultTo';
import evolve from 'ramda/es/evolve';
import ifElse from 'ramda/es/ifElse';
import map from 'ramda/es/map';
import mergeRight from 'ramda/es/mergeRight';
import pick from 'ramda/es/pick';
import pipe from 'ramda/es/pipe';
import pluck from 'ramda/es/pluck';
import tap from 'ramda/es/tap';
import uniq from 'ramda/es/uniq';
import ramdaUseWith from 'ramda/es/useWith';
import { Maybe } from 'ramda-fantasy';

import Actions from '../Actions';
import { globalSettingStore } from '../GlobalSettingStore';
import { stores } from '../Stores';

class MemberActions {
  private fetchCache = new FetchCache<MEMBER_ID, Member>();

  private reFetchMemberIds = [];

  private idQueue = [];
  private deferredQueue = [];

  constructor(private actions: Actions) {}

  fetchLeaveMembersByLogs = (logs: Log[]) => {
    pipe<any, any, any, any>(pluck('senderId'), uniq, this.fetchBulkMembers)(logs);
  };

  fetchMember = (memberId: MEMBER_ID): Promise<Member> => {
    return this.fetchBulkMembers([memberId]).then(([member]) => member);
  };

  fetchMemberForce = (memberId: MEMBER_ID): Promise<Member> => {
    return (api.member.fetchMemberById(memberId) as any)
      .then(tap(this.handleReferences))
      .then(this.handleFetchedMember)
      .then(() => membersStore.getById(memberId))
      .catch(() => {});
  };

  getOrFetchMemberById = curry((memberId: MEMBER_ID, channelId: CHANNEL_ID): Promise<SharedChannelMember> => {
    const member = interTenantMembersStore.getById(memberId);
    if (member) {
      return Promise.resolve(member);
    }
    const channel = channelsStore.getById(channelId);
    return channel && isSharedChannel(channel)
      ? this.actions.channelMembersActions.fetchInterTenantMember(channel, memberId)
      : this.fetchMember(memberId);
  });

  private fetchMembers = (membersId: MEMBER_ID[]): Promise<Member[]> => {
    return maybeOr(
      () => Promise.resolve(membersStore.getMembersById(membersId)),
      (unFetchedMembersId: string[]) => {
        return this.fetchCache.wrapFetchList(
          unFetchedMembersId,
          pipe(
            api.member.fetchMembers,
            then(tap(this.handleReferences)),
            then(tap(this.handleFetchedMembers)),
            then(prop('contents'))
          )
        );
      },
      this.filterUnFetchedMembersIdAndQueueReFetch(membersId)
    );
  };

  private reFetchMembers = debounce(() => {
    const membersId = uniq(this.reFetchMemberIds);
    this.reFetchMemberIds = [];
    if (membersId.length === 0) {
      return;
    }
    this.fetchCache.wrapFetchList(membersId, (membersId) => {
      GA.writeWillMemberFetch(`reFetch id list: ${membersId.join(',')}`, membersId.length);
      return Promise.resolve(membersId.map(interTenantMembersStore.getById));
    });
  }, SECOND);

  public fetchBulkMembers = (membersId: MEMBER_ID[]): Promise<Member[]> => {
    this.idQueue = uniq(this.idQueue.concat(membersId));
    const deferred = defer();
    this.deferredQueue.push(deferred);
    this.debouncedCallFetch();
    if (this.idQueue.length > 100) {
      this.debouncedCallFetch.flush();
    }
    return deferred.promise.then(() => membersStore.getMembersById(membersId));
  };

  private debouncedCallFetch = throttle(() => {
    const idQueue = this.idQueue;
    const deferredQueue = this.deferredQueue;
    this.idQueue = [];
    this.deferredQueue = [];

    const max = Math.ceil(idQueue.length / 100);
    const promiseList = [];

    for (let i = 0; i < max; i++) {
      promiseList.push(this.fetchMembers(idQueue.slice(i * 100, (i + 1) * 100)));
    }

    Promise.all(promiseList).then(() => {
      deferredQueue.forEach(({ resolve }) => resolve());
    });
  }, 200);

  fetchPosition = (positionId: POSITION_ID): FutureInstance<never, PositionRef> => {
    return pipe<any, any, any, any>(
      encaseP(api.department.fetchJobPosition),
      map(prop('content')),
      map(tap(membersStore.setPosition))
    )(positionId);
  };

  fetchUser = (): FutureInstance<never, MemberResponse> => {
    return pipe<string, any, any, any>(
      encaseP(api.me.fetchUser),
      map(tap(this.handleReferences)),
      map(tap(this.handleFetchedMember))
    )(null);
  };

  sendLocalStatus = (websocketActions: WebsocketActions = this.actions.websocketActions) => {
    pipe(
      this.actions.userActions.getLocalStatus,
      then(defaultTo(MemberStatus.Active)),
      then(websocketActions.updateStatus)
    )(userStore.id);
  };

  inviteTenantMembers = (form) => {
    const organizationId = userStore.orgId;
    const { name: inviterName } = membersStore.getById(userStore.id);
    const { invitee, role: invitedOrganizationRole, locale, expirationDate, invitationReason } = form.values();
    const inviteParam = {
      invitedOrganizationRole,
      locale,
      organizationId,
      inviterName,
      projectId: null,
      inviteeGuestExpirationDate: expirationDate,
      invitationReason,
    };
    const validInvitee = reject(prop('invalid'), invitee);
    const { inviteMember, inviteGuest } = userStore.effectiveFeatures || {};
    const approve = (invitedOrganizationRole === 'member' ? inviteMember : inviteGuest) === 'approve';
    if (isEmpty(validInvitee)) return;
    const inviteParams = pipe(
      map(ramdaUseWith(rename({ value: 'inviteeEmail' }), [pick(['value'])])),
      map(mergeRight(inviteParam))
    )(validInvitee);
    pipe<any, any, any>(
      encaseP(api.member.inviteTenantMembers),
      runFutureValue((result: TenantInvitedMemberResponse) => {
        uiState.openModal({
          name: MODAL_NAMES.INVITE_TENANT_MEMBERS_RESULT,
          args: { result, inviteParams, approve },
        });
      })
    )(inviteParams);
  };

  inviteSharedChannel = (form) => {
    const { channelId, invitee, locale, tenantId, tenantDomain, token } = form.values();
    const validInvitee = reject(prop('invalid'), invitee);
    if (isEmpty(validInvitee)) return;
    const inviteParam = {
      locale,
      tenantId,
      tenantDomain,
      token,
    };

    const inviteParams = pipe(
      map(ramdaUseWith(rename({ value: 'inviteeEmail' }), [pick(['value'])])),
      map(mergeRight(inviteParam))
    )(validInvitee);

    pipe(
      encaseP(interTenantApi.member.inviteSharedChannelMember(channelId)),
      runFutureValue(() => {
        uiState.openModal({
          name: MODAL_NAMES.INVITE_TENANT_MEMBERS_RESULT,
          args: { result: [{ status: 'success' }], inviteParams, approve: false },
        });
      })
    )(inviteParams);
  };

  private handleFetchedMember = ({ content: member }: MemberResponse) => {
    membersStore.mergeMember(member);
  };

  private filterUnFetchedMembersIdAndQueueReFetch = (memberIds: MEMBER_ID[] = []): Maybe<MEMBER_ID[]> => {
    const unFetchedMemberIds = memberIds.filter((id) => !interTenantMembersStore.getById(id));
    const now = Date.now();
    const fetchedMemberIds = memberIds.filter(
      (id) => membersStore.getById(id) && membersStore.isMemberCacheExpired(id, now)
    );
    this.reFetchMemberIds.push(...fetchedMemberIds);
    this.reFetchMembers();
    if (this.reFetchMemberIds.length > 100) {
      this.reFetchMembers.flush();
    }
    return ifElse(isEmpty, () => Maybe.Nothing(), Maybe.Just)(unFetchedMemberIds) as Maybe<MEMBER_ID[]>;
  };

  private handleFetchedMembers = ({ contents: members }) => {
    membersStore.mergeMembers(members);
    membersStore.updateFetchedAtList(members);
  };

  private handleReferences = ({ references }) => {
    evolve({
      departmentMap: organizationChartStore.mergeDepartmentMap,
      positionMap: membersStore.mergePositionMap,
      rankMap: membersStore.mergeRankMap,
    })(references || {});
  };

  public openProfilePopper = ({
    referenceNode,
    options,
    memberId,
    channelId,
  }: {
    referenceNode: HTMLElement;
    memberId: MEMBER_ID;
    channelId: CHANNEL_ID;
    options: Partial<Options>;
  }) => {
    this.getOrFetchMemberById(memberId, channelId).then((member) => {
      if (isEmpty(member)) return;
      uiState.openPopper({
        name: PopperType.Profile,
        args: { referenceNode, memberId, options, channelId, type: PROFILE_TYPE.MEMBER },
      });
    });
  };

  public getInquireUrl() {
    const { protocol, host } = window.location;
    const inquireUrl = `${protocol}//${host}`;
    const helpMailDomain = stores.helpMailDomain;

    return globalSettingStore.canUseMail
      ? `${inquireUrl}/mail/write/new?to=${helpMailDomain}`
      : isNative()
      ? `mailto:${helpMailDomain}`
      : '';
  }
}

export default MemberActions;
